// src/components/admin/AdminBlog.jsx
import { useState, useEffect } from 'react';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy, Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase';

const EMPTY_FORM = {
  title: '', slug: '', excerpt: '', content: '', category: '',
  imageUrl: '', publishedDate: '',
  metaTitle: '', metaDescription: '', metaKeywords: '',
};

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  async function fetchPosts() {
    setLoading(true);
    try {
      const q = query(collection(db, 'blog_posts'), orderBy('publishedAt', 'desc'));
      const snap = await getDocs(q);
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchPosts(); }, []);

  function openCreate() {
    setEditingPost(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    setMsg(null);
  }

  function openEdit(post) {
    setEditingPost(post);
    const pubDate = post.publishedAt?.toDate
      ? post.publishedAt.toDate().toISOString().split('T')[0]
      : (post.date ? '' : '');
    setForm({
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      category: post.category || '',
      imageUrl: post.imageUrl || post.img || '',
      publishedDate: pubDate,
      metaTitle: post.metaTitle || '',
      metaDescription: post.metaDescription || '',
      metaKeywords: post.metaKeywords || '',
    });
    setShowForm(true);
    setMsg(null);
  }

  function closeForm() { setShowForm(false); setEditingPost(null); setMsg(null); }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => {
      const next = { ...p, [name]: value };
      // Auto-generate slug from title when creating
      if (name === 'title' && !editingPost && !p.slug) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const publishedAt = form.publishedDate
        ? Timestamp.fromDate(new Date(form.publishedDate))
        : serverTimestamp();
      const data = {
        title: form.title,
        slug: form.slug || slugify(form.title),
        excerpt: form.excerpt,
        content: form.content,
        category: form.category,
        imageUrl: form.imageUrl,
        publishedAt,
        metaTitle: form.metaTitle || form.title,
        metaDescription: form.metaDescription || form.excerpt,
        metaKeywords: form.metaKeywords,
        updatedAt: serverTimestamp(),
      };
      if (editingPost) {
        await updateDoc(doc(db, 'blog_posts', editingPost.id), data);
        setMsg({ type: 'success', text: 'Post updated.' });
      } else {
        await addDoc(collection(db, 'blog_posts'), { ...data, createdAt: serverTimestamp() });
        setMsg({ type: 'success', text: 'Post created.' });
      }
      await fetchPosts();
      closeForm();
    } catch (err) {
      setMsg({ type: 'error', text: 'Error saving post: ' + err.message });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(post) {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'blog_posts', post.id));
      await fetchPosts();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  }

  const formatDate = (post) => {
    if (post.publishedAt?.toDate) return post.publishedAt.toDate().toLocaleDateString();
    return post.date || '—';
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <h2 className="title-4">Blog Posts</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ New Post</button>
      </div>

      {msg && (
        <div style={{ padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', background: msg.type === 'success' ? '#dcfce7' : '#fee2e2', color: msg.type === 'success' ? '#16a34a' : '#dc2626', fontSize: '14px' }}>
          {msg.text}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="admin-form-panel">
          <h3 className="title-6" style={{ marginBottom: '24px' }}>{editingPost ? `Edit: ${editingPost.title}` : 'Create New Post'}</h3>
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Basic Info */}
            <div className="admin-form-section-label">Post Content</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input name="title" className="form-input" value={form.title} onChange={handleChange} required placeholder="Post title" />
              </div>
              <div className="form-group">
                <label className="form-label">URL Slug *</label>
                <input name="slug" className="form-input" value={form.slug} onChange={handleChange} required placeholder="post-url-slug" />
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <input name="category" className="form-input" value={form.category} onChange={handleChange} placeholder="e.g. Cloud Services" />
              </div>
              <div className="form-group">
                <label className="form-label">Publish Date</label>
                <input name="publishedDate" type="date" className="form-input" value={form.publishedDate} onChange={handleChange} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Cover Image URL</label>
              <input name="imageUrl" className="form-input" value={form.imageUrl} onChange={handleChange} placeholder="https://..." />
            </div>
            <div className="form-group">
              <label className="form-label">Excerpt / Summary *</label>
              <textarea name="excerpt" className="form-textarea" value={form.excerpt} onChange={handleChange} required placeholder="Brief summary shown in blog listing..." style={{ height: '80px' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Content (supports ## headings and paragraphs) *</label>
              <textarea name="content" className="form-textarea" value={form.content} onChange={handleChange} required placeholder="## Section Title&#10;&#10;Paragraph content here..." style={{ height: '300px', fontFamily: 'monospace', fontSize: '14px' }} />
            </div>

            {/* SEO Section */}
            <div className="admin-form-section-label" style={{ marginTop: '8px' }}>SEO / Meta Data</div>
            <div className="form-group">
              <label className="form-label">Meta Title <span className="body-sm" style={{ color: 'var(--token-desc)' }}>(defaults to post title)</span></label>
              <input name="metaTitle" className="form-input" value={form.metaTitle} onChange={handleChange} placeholder={form.title || 'SEO page title'} />
            </div>
            <div className="form-group">
              <label className="form-label">Meta Description <span className="body-sm" style={{ color: 'var(--token-desc)' }}>(defaults to excerpt)</span></label>
              <textarea name="metaDescription" className="form-textarea" value={form.metaDescription} onChange={handleChange} placeholder={form.excerpt || 'SEO description (155 characters recommended)'} style={{ height: '80px' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Meta Keywords <span className="body-sm" style={{ color: 'var(--token-desc)' }}>(comma-separated)</span></label>
              <input name="metaKeywords" className="form-input" value={form.metaKeywords} onChange={handleChange} placeholder="keyword1, keyword2, keyword3" />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editingPost ? 'Update Post' : 'Publish Post'}
              </button>
              <button type="button" className="btn btn-outline" onClick={closeForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Posts Table */}
      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--token-desc)' }}>Loading...</div>
      ) : posts.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--token-desc)', background: '#fff', borderRadius: '8px' }}>
          No posts yet. Click "+ New Post" to create one.
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Slug</th>
              <th>Published</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map(post => (
              <tr key={post.id}>
                <td><strong>{post.title}</strong></td>
                <td>{post.category || '—'}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--token-desc)' }}>{post.slug || '—'}</td>
                <td>{formatDate(post)}</td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-outline" style={{ padding: '4px 12px', height: 'auto', fontSize: '13px' }} onClick={() => openEdit(post)}>Edit</button>
                    <button style={{ padding: '4px 12px', height: 'auto', fontSize: '13px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer' }} onClick={() => handleDelete(post)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}